// src/pages/Batches/subpages/BatchShow.tsx
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tab, Tabs } from 'react-bootstrap';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../../components/default/DefaultCard';
import LazyLoading from '../../../components/LazyLoading';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';

import useBatchService from '../../../app/services/hooks/useBatchService';
import { BatchDataForm } from '../../../app/models/Batch';
import BatchFormContainer from '../components/BatchFormContainer';
import BatchHistoryTab from '../components/tables/BatchHistoryTab';
import BatchSummaryTab from '../components/tables/BatchSummaryTab';
import useNavigationPage from '../../../hooks/useNavigationPage';

type RouterParams = { id: string };
type SelectOpt = { id: number | string; name: string };

const BatchShow: React.FC = () => {
  const { id } = useParams<RouterParams>();
  const batchId = Number(id);
  const { navigationPage } = useNavigationPage();

  const { showLoading, hideLoading } = useContext(AppContext);
  const { editBatch, fetchingEditBatch } = useBatchService();

  const editRef = useRef(editBatch);
  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);
  useEffect(() => { editRef.current = editBatch; }, [editBatch]);
  useEffect(() => { showLoadingRef.current = showLoading; }, [showLoading]);
  useEffect(() => { hideLoadingRef.current = hideLoading; }, [hideLoading]);

  const [verifying, setVerifying] = useState<boolean>(true);

  const [batch, setBatch] = useState<BatchDataForm>({
    name: '',
    batch_category: 'COLLECTION',
    quantity: 0,
    is_active: true,
  });

  const [projects, setProjects] = useState<SelectOpt[]>([]);
  const [centers, setCenters] = useState<SelectOpt[]>([]);
  const [units, setUnits] = useState<SelectOpt[]>([]);
  const [batchCategories, setBatchCategories] = useState<SelectOpt[]>([]);
  const [societies, setSocieties] = useState<SelectOpt[]>([]);
  const [warehouses, setWarehouses] = useState<SelectOpt[]>([]);
  const [products, setProducts] = useState<SelectOpt[]>([]);
  const [history, setHistory] = useState<any[]>([]);

  const load = useCallback((bid: number) => {
    showLoadingRef.current?.('loading', 'Cargando lote...');
    editRef.current(bid, {
      onSuccess: (resp: ServiceResponse) => {
        setBatch(resp.data.batch as BatchDataForm);
        setProjects(resp.data.projects || []);
        setCenters(resp.data.centers || []);
        setUnits(resp.data.units || []);
        setBatchCategories(resp.data.batch_categories || []);
        setSocieties(resp.data.societies || []);
        setWarehouses(resp.data.warehouses || []);
        setProducts(resp.data.products || []);
        setHistory(resp.data.history || []);
        hideLoadingRef.current?.();
        setVerifying(false);
      },
      onError: (resp: ServiceResponse) => {
        hideLoadingRef.current?.();
        toast.error(resp.message, { autoClose: 2500 });
        setVerifying(false);
      }
    });
  }, []);

  useEffect(() => { if (Number.isFinite(batchId)) load(batchId); }, [batchId, load]);

  // Función no-op para el prop action (requerido por BatchFormContainer)
  const noOpAction = useCallback(() => {
    // Intencionalmente vacío - el formulario está en modo disabled
  }, []);

  const breadcrumbs: BreadcrumbsItem[] = [
    { name: 'Lotes', url: '/batches', isActive: false },
    { name: `Ver Lote #${batchId}`, url: `/batch/${batchId}/show`, isActive: true },
  ];

  const formatDate = (d?: string | null) => {
    if (!d) return '';
    try { const date = new Date(d); return Number.isNaN(date.getTime()) ? (d as string) : date.toLocaleDateString(); }
    catch { return d as string; }
  };

  const createdByName =
    (batch as any).created_by_name ||
    (batch as any).created_by_fullname ||
    [ (batch as any)?.created_by?.first_name, (batch as any)?.created_by?.last_name ].filter(Boolean).join(' ') ||
    (batch as any)?.created_by?.username || '';

  const updatedByName =
    (batch as any).updated_by_name ||
    (batch as any).updated_by_fullname ||
    [ (batch as any)?.updated_by?.first_name, (batch as any)?.updated_by?.last_name ].filter(Boolean).join(' ') ||
    (batch as any)?.updated_by?.username || '';

  const availabilityStatusLabel =
    (batch as any).availability_status_label ??
    (batch.availability_status === 'COLLECTED'
      ? 'Acopiado'
      : batch.availability_status === 'FREE'
        ? 'Libre disponibilidad'
        : (batch.availability_status as string) || '');

  if (verifying) {
    return (
      <>
        <Breadcrumbs pageSection={`Ver Lote #${batchId}`} breadcrumbs={breadcrumbs} />
        <DefaultCard><LazyLoading height={300} /></DefaultCard>
      </>
    );
  }

  return (
    <>
      <Breadcrumbs pageSection={`Ver Lote #${batchId}`} breadcrumbs={breadcrumbs} />

      <Tabs defaultActiveKey="general" id="batch-show-tabs" className="mb-3">
        {/* === TAB 1: Datos Generales === */}
        <Tab eventKey="general" title="Datos Generales">
          {/* Fila con formulario y metadata */}
          <div className="row">
            {/* Izquierda: Formulario en solo lectura */}
            <div className="col-12 col-lg-8">
              <DefaultCard>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Datos del lote</h5>
                </div>
                <BatchFormContainer
                  fetching={fetchingEditBatch}
                  action={noOpAction}
                  cancel={() => navigationPage('/batches')}
                  batch={batch}
                  setBatch={setBatch}
                  projects={projects}
                  setProjects={setProjects}
                  centers={centers}
                  setCenters={setCenters}
                  units={units}
                  setUnits={setUnits}
                  batchCategories={batchCategories}
                  setBatchCategories={setBatchCategories}
                  societies={societies}
                  setSocieties={setSocieties}
                  warehouses={warehouses}
                  setWarehouses={setWarehouses}
                  products={products}
                  setProducts={setProducts}
                  errorFields={undefined}
                  disabled={true}
                />
              </DefaultCard>
            </div>

            {/* Derecha: Metadata de auditoría */}
            <div className="col-12 col-lg-4">
              <DefaultCard>
                <h5 className="mb-3">Datos del creador</h5>
                <div className="mb-2">
                  <label className="form-label">Creado por</label>
                  <input className="form-control" value={createdByName} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fecha de creación</label>
                  <input className="form-control" value={formatDate((batch as any).created_at)} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Editado por</label>
                  <input className="form-control" value={updatedByName} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fecha de actualización</label>
                  <input className="form-control" value={formatDate((batch as any).updated_at)} disabled />
                </div>
                <hr />
                <div className="mb-2">
                  <label className="form-label">Activo</label>
                  <input className="form-control" value={batch.is_active ? 'Sí' : 'No'} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Estado del lote</label>
                  <input className="form-control" value={availabilityStatusLabel} disabled />
                </div>
              </DefaultCard>
            </div>
          </div>
        </Tab>
      </Tabs>

      {/* Sección de información adicional debajo de Datos Generales */}
      <div className="mt-4">
        <Tabs defaultActiveKey="summary" id="batch-info-tabs" className="mb-3">
          {/* === TAB 1: Resumen === */}
          <Tab eventKey="summary" title="Resumen">
            <BatchSummaryTab batch={batch} />
          </Tab>

          {/* === TAB 2: Historial === */}
          <Tab eventKey="history" title="Historial">
            <BatchHistoryTab history={history} />
          </Tab>
        </Tabs>
      </div>
    </>
  );
};

export default BatchShow;